import { useCallback, useState } from 'react';

/** Read a JSON value from localStorage; any failure (private mode, quota) → fallback. */
export function readStorage<T>(key: string, fallback: T, guard: (v: unknown) => v is T): T {
  try {
    if (typeof window === 'undefined') return fallback;
    const raw = window.localStorage.getItem(key);
    if (raw === null) return fallback;
    const parsed: unknown = JSON.parse(raw);
    return guard(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
}

export function writeStorage(key: string, value: unknown): void {
  try {
    if (typeof window === 'undefined') return;
    if (value === undefined || value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage unavailable — the preference is just not remembered.
  }
}

/** UI preference persisted per device (never business data). Client-only SPA: read on init. */
export function usePersistentState<T>(key: string, fallback: T, guard: (v: unknown) => v is T) {
  const [value, setValue] = useState<T>(() => readStorage(key, fallback, guard));
  const update = useCallback(
    (next: T) => {
      setValue(next);
      writeStorage(key, next);
    },
    [key],
  );
  return [value, update] as const;
}

export const isBoolean = (v: unknown): v is boolean => typeof v === 'boolean';
export const isString = (v: unknown): v is string => typeof v === 'string';
export const isPositiveInt = (v: unknown): v is number =>
  typeof v === 'number' && Number.isInteger(v) && v > 0;
